import { Injectable } from '@angular/core';
import { isEmptyField, isNullOrUndefined } from '../common-utils';
@Injectable({
  providedIn: 'root'
})
export class VlDataService {

  constructor() { }

  deepMerge(src: any, dest: any, options?: any): any {
    const result: any = {};
    // tslint:disable-next-line: forin
    for (const i in src) {
      const type = typeof src[i];
      if (!isNullOrUndefined(options?.customMerge?.[i])) {
        result[i] = this.customMerge(src, dest, i, options);
      } else if (!isNullOrUndefined(src) && src[i] !== null && type == 'object' && !(src[i] instanceof Array)) {
        if (!(dest?.[i] instanceof Array)) {
          result[i] = (!isNullOrUndefined(dest?.[i])) ? this.deepMerge(src[i], dest[i], options) : src[i];
        } else {
          result[i] = dest[i];
        }
      } else if (!isNullOrUndefined(src?.[i]) && src?.[i] instanceof Array) {
        result[i] = this.mergeArray(src, dest, i, options);
      } else {
        result[i] = dest?.[i] ?? src[i];
      }
    }
    for (const i in dest) {
      if (isNullOrUndefined(result[i])) {
        result[i] = dest[i];
      }
    }
    return result;
  }

  customMerge(src: any, dest: any,  i: any, options?: any) {
    // tslint:disable-next-line: triple-equals
    if (options.customMerge[i] == 'dest') {
      return !isNullOrUndefined(dest[i]) ? dest[i] : null;
      // tslint:disable-next-line: triple-equals
    } else if (options.customMerge[i] == 'src') {
      return!isNullOrUndefined(src[i]) ? src[i] : null;
    } else {
      return options.customMerge[i](src[i], dest[i]);
    }
  }

  mergeArray(src: any, dest: any,  i: any, options?: any) {
    if (!isNullOrUndefined(options?.array) && options?.array == 'merge') {
      return (dest?.[i]?.length > 0) ? src[i].concat(dest[i]) : src[i];
    } else if (!isNullOrUndefined(options?.array) && options?.array == 'override') {
      return dest?.[i] ?? src[i];
    } else {
      return (dest?.[i]?.length > 0) ? src[i].concat(dest[i]) : src[i];
    }
  }
  
  deepClone(object: any): any {
    if (isEmptyField(object)) {
      return;
    }
    // Check for null or undefined
    if (object === null || object === undefined) {
      return object;
    }

    // Handle primitive types (no need to clone)
    if (typeof object !== 'object') {
      return object;
    }

    // Handle Date object
    if (object instanceof Date) {
      return new Date(object.getTime());
    }

     // Handle File type
     if (object instanceof File) {
      return new File([object], object.name, {
        type: object.type,
        lastModified: object.lastModified,
      }) ;
    }

    // Handle Array
    if (Array.isArray(object)) {
      return object.map(item => this.deepClone(item)); // Recursively deep clone array items
    }

    // Handle Object
    const cloneObj: any = {};
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        const property = object[key];
        cloneObj[key] = this.deepClone(property); // Recursively clone each property
      }
    }

    return cloneObj;
  }

  deepCompare(obj1: any, obj2: any): boolean {
    // Loop through properties in object 1
    // tslint:disable-next-line: forin
    for (const p in obj1) {
      // Check property exists on both objects
      if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) {
        return false;
      }
  
      // Helper function to compare properties
      if (!this.compareProperty(obj1[p], obj2[p], p)) {
        return false;
      }
    }
  
    // Check object 2 for any extra properties
    for (const p in obj2) {
      if (obj2.hasOwnProperty(p) && typeof obj1[p] === 'undefined') {
        return false;
      }
    }
  
    return true;
  }
  
  compareProperty(value1: any, value2: any, propName: string): boolean {
    switch (typeof value1) {
      // Deep compare objects
      case 'object':
        return this.deepCompare(value1, value2);
      // Compare function code
      case 'function':
        return typeof value2 !== 'undefined' && (propName === 'compare' || value1.toString() === value2.toString());
      // Compare values
      default:
        return value1 === value2;
    }
  }
  
  flattenObject(data: any) {
    return data.map((value: any, key: any) => this.flattenOneObject(value));
  }

  flattenOneObject(ob: any) {
    if (typeof ob === 'string') {
      return ob;
    }
    const toReturn: any = {};
    for (const i in ob) {
      if (!ob.hasOwnProperty(i)) { continue; }
      // tslint:disable-next-line: triple-equals
      if ((typeof ob[i]) == 'object' && !(ob[i] instanceof Array)) {
        const flatObject = this.flattenOneObject(ob[i]);
        for (const x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) { continue; }
          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
    return toReturn;
  }

  deflattenObject(obj: any) {
    const type = typeof obj;
    const result = {};
    if (type == 'object' && !(obj instanceof Array)) {
      // tslint:disable-next-line: forin
      for (const i in obj) {
        const allKeys = i.split('.');
        // tslint:disable-next-line: prefer-const
        // tslint:disable-next-line: one-variable-per-declaration
        let temp: any = result, length = allKeys.length;
        const lastKey = allKeys[length - 1];
        allKeys.forEach((key, i) => {
          if (i < (length - 1)) {
            temp[key] = temp[key] || {};
            temp = temp[key];
          }
        });
        temp[lastKey] = obj[i];
      }
      return result;
    } else {
      return obj;
    }
  }

  clearEmptyFields(data: any, emptyFillWith?: any) {
    if (isEmptyField(emptyFillWith)) {
      emptyFillWith = null;
    }
    const returnableObject: any = {};
    let flattenData: any;
    let isObjectEmpty = true;
    if (Array.isArray(data)) {
      flattenData = this.flattenObject(data);
      flattenData.forEach((singleObject: any) => {
        [isObjectEmpty] = this.clearOneObjectEmptyFields(singleObject, isObjectEmpty, emptyFillWith);
      });
    } else {
      flattenData = this.flattenOneObject(data);
      [flattenData, isObjectEmpty] = this.clearOneObjectEmptyFields(flattenData, isObjectEmpty, emptyFillWith);
    }
    const deflattenObject = this.deflattenObject(flattenData);
    returnableObject.data = deflattenObject;
    returnableObject.isObjectEmpty = isObjectEmpty;
    return deflattenObject;
  }

  clearOneObjectEmptyFields(flattenData: any, isObjectEmpty: any, emptyFillWith: any) {
    for (const val of Object.keys(flattenData)) {
      if (isNullOrUndefined(flattenData[val]) || flattenData[val] === '') {
        flattenData[val] = emptyFillWith;
      }
    }
    return [isObjectEmpty];
  }

  checkOneObjectForEmpty(flattenData: any, isObjectEmpty: any) {
    for (const val of Object.keys(flattenData)) {
      if (isNullOrUndefined(flattenData[val]) || flattenData[val] === '') {
        flattenData[val] = null;
      } else {
        isObjectEmpty = false;
      }
    }
    return isObjectEmpty;
  }

  isEmptyEveryField(data: any) {
    let isObjectEmpty = true;
    let flattenData: any = {};
    if (Array.isArray(data)) {
      flattenData = this.flattenObject(data);
      flattenData.forEach((singleObject: any) => {
        if (!isObjectEmpty) {
          isObjectEmpty = this.checkOneObjectForEmpty(singleObject, isObjectEmpty);
        }
      });
    } else {
      flattenData = this.flattenOneObject(data);
      isObjectEmpty = this.checkOneObjectForEmpty(flattenData, isObjectEmpty);
    }
    return isObjectEmpty;
  }

  convertToIntegerFromArrayOfObjects(data: any[], fields: string[]) {
    return data.map(element => this.convertToIntegerFromObject(element, fields));
  }

  convertToIntegerFromObject(object: any, fields: string[]) {
    for (const key of Object.keys(object)) {
      if (fields.indexOf(key) != -1) {
        object[key] = parseFloat(object[key]);
      }
    }
    return object;
  }

}
